import { useQuery } from '@apollo/react-hooks';
import { Box, createStyles, Grid, makeStyles, Paper } from '@material-ui/core';
import { Breadcrumbs, BreadcrumbsText, Button, ExtendedTheme, Heading, Link, Loader, TextBlock, UserInterfaceCheckSmall } from 'neon-design-system';
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { GET_PREMIUM_CHANNEL_BY_ID } from '../../../gql/channels';
import { Channel, ChannelSubscription } from '../../../shared/types';
import { baseUrls } from '../../../utils/baseUrls.utils';
import {
	getChannelSubscriptionConfirmationLink,
	getChannelSubscriptionInterestConfirmLink
} from '../../../utils/link.utils';
import { EmailNeonSales } from '../../ui/EmailNeonSales';
import SectionHeader from '../../ui/SectionHeader';
import { GET_USER_PREMIUM_CHANNEL_SUBSCRIPTIONS } from '../../../gql/users';
import { useRequestFreeTrial } from '../../hooks/use-request-free-trial';

const useStyles = makeStyles<ExtendedTheme>((theme) => createStyles({
	listItem: {
		display: 'flex',
		alignItems: 'center',
		'&>svg': {
			marginRight: theme.spacing(1)
		}
	},
	offset: {
		marginTop: theme.spacing(6),
		[theme.breakpoints.down('sm')]: {
			marginTop: theme.spacing(2)
		}
	},
	blueGray: {
		color: theme.themeColors.blueGrey[200]
	}
}));

export const PremiumChannelSubscriptionScreen = () => {
	const { channelId } = useParams<{ channelId: string }>();
	const { data, loading } = useQuery<{ channel: Channel }>(GET_PREMIUM_CHANNEL_BY_ID, {
		variables: { id: channelId },
		fetchPolicy: 'cache-first'
	});
	const { data: subscriptions, loading: loadingSubscriptions } = useQuery(GET_USER_PREMIUM_CHANNEL_SUBSCRIPTIONS, { fetchPolicy: 'network-only' });
	const [requestTrial, loadingTrialRequest] = useRequestFreeTrial();
	const history = useHistory();
	const classes = useStyles();

	const currentSubscription = subscriptions?.premiumChannelSubscriptions?.find((pc: ChannelSubscription) => pc.channelId === channelId);
	const existingSubscription = !!currentSubscription;

	const requestTrialHandler = () => requestTrial(channelId).then(
		() => history.push(getChannelSubscriptionInterestConfirmLink(channelId) + '?trial')
	);

	const buyPremiumChannelAccessHandler = () => {
		history.push(getChannelSubscriptionConfirmationLink(channelId))
	}

	if (loading || loadingSubscriptions || loadingTrialRequest) {
		return <Loader />;
	}

	return <Grid container spacing={4}>
		<Grid item xs={12}>
			<Breadcrumbs>
				<Link to={baseUrls.channels.main}>All channels</Link>
				<Link to={`${baseUrls.browse.channelMain}/${data?.channel._id}`}>{data?.channel.name}</Link>
				<BreadcrumbsText>Subscribe</BreadcrumbsText>
			</Breadcrumbs>
		</Grid>
		<Grid item xs={12} md={8}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<SectionHeader
						id="premium-channel-subscription-confirmation-header"
						title={`Subscribe to ${data?.channel.name}`}
						subtitle={data?.channel.description} />
				</Grid>
				<Grid item xs={12}>
					<Grid container spacing={1}>
						<Grid item xs={12}>
							<TextBlock styleName="largeBody1" color="white">${data?.channel.price}/month*</TextBlock>
						</Grid>
						<Grid item xs={12}>
							<TextBlock ink="muted">*Price excludes applicable sales taxes</TextBlock>
						</Grid>
					</Grid>
				</Grid>
				<Grid item xs={12}>
					<Grid container spacing={1}>
						<Grid item xs={12} className={classes.listItem}>
							<UserInterfaceCheckSmall /> <TextBlock>Billed monthly, subscription renews until cancelled</TextBlock>
						</Grid>
						<Grid item xs={12} className={classes.listItem}>
							<UserInterfaceCheckSmall /> <TextBlock>Access on any device</TextBlock>
						</Grid>
						<Grid item xs={12} className={classes.listItem}>
							<UserInterfaceCheckSmall /> <TextBlock>Cancel anytime</TextBlock>
						</Grid>
					</Grid>
				</Grid>
				<Grid item xs={12}>
					<Button
						size="large"
						colorType="primary"
						onClick={buyPremiumChannelAccessHandler}
					>
						Buy Premium access
					</Button>
				</Grid>
				{!loadingSubscriptions && !existingSubscription && <>
					<Grid item xs={12}>
						<Grid container spacing={1}>
							<Grid item xs={12}>
								<TextBlock styleName="largeBody1" color="white" className={classes.offset}>Alternatively, you can request a free trial</TextBlock>
							</Grid>
							<Grid item xs={12}>
								<TextBlock ink="muted">Get started with Channels for free and gain access to market leading commentary from industry experts.</TextBlock>
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={12}>
						<Button size="large" colorType="primary" onClick={requestTrialHandler} busy={loadingTrialRequest}>Request FREE trial now</Button>
					</Grid>
					<Grid item xs={12}>
						<TextBlock styleName="footnote" className={classes.blueGray}>
							Once approved, you’ll have <strong>full access for {data?.channel.defaultTrialDurationDays} days.</strong>
						</TextBlock>
					</Grid>
				</>}
			</Grid>
		</Grid>
		<Grid item xs={12} md={4}>
			<Paper>
				<Box p={4}>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<Heading styleName="headline1" color="white">If you need further assistance</Heading>
						</Grid>
						<Grid item xs={12}>
							<TextBlock><strong>Email us at</strong></TextBlock>
							<TextBlock><EmailNeonSales /></TextBlock>
						</Grid>
					</Grid>
				</Box>
			</Paper>
		</Grid>
	</Grid>;
}
